/**
 * Recover a tab that outlived a UI release.
 *
 * Each release renames every lazy-loaded chunk, and the old ones are gone from the new image.
 * A tab opened before the release fails on its next lazy page with "Failed to fetch dynamically
 * imported module" (prod, 2026-11-04: /onboarding/setup right after sign-up). index.html is served
 * no-store, so one full reload loads the new build. The reload happens at most once a minute: a
 * chunk that is truly missing from the current build shows the error instead of looping.
 */

export const RELOAD_GUARD_KEY = 'qyngent:chunk-reload-at'
const RELOAD_WINDOW_MS = 60_000

const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module', // Chromium
  'Importing a module script failed', // Safari
  'error loading dynamically imported module', // Firefox
  'Unable to preload CSS', // Vite's CSS preload
]

interface ReloadWindow {
  location: { reload: () => void }
}

interface GuardStorage {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
}

function browserStorage(): GuardStorage | null {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

export function isChunkLoadError(err: unknown): boolean {
  if (!(err instanceof Error)) return false
  if (err.name === 'ChunkLoadError') return true
  return CHUNK_ERROR_PATTERNS.some((pattern) => err.message.includes(pattern))
}

/** Reload the page unless it already reloaded for this within the last minute. */
export function reloadForNewBuild(
  win: ReloadWindow = window,
  storage: GuardStorage | null = browserStorage(),
  now: number = Date.now(),
): boolean {
  try {
    const last = Number(storage?.getItem(RELOAD_GUARD_KEY))
    if (last && now - last < RELOAD_WINDOW_MS) return false
    storage?.setItem(RELOAD_GUARD_KEY, String(now))
  } catch {
    // No storage (private mode): reload anyway. A loop would need storage to fail AND the
    // chunk to be missing from the current build.
  }
  win.location.reload()
  return true
}

/** Listen for Vite's failed-chunk event and reload once instead of letting the page break. */
export function installChunkReload(
  win: ReloadWindow & Pick<Window, 'addEventListener'> = window,
  storage: GuardStorage | null = browserStorage(),
  clock: () => number = Date.now,
): void {
  win.addEventListener('vite:preloadError', (event: Event) => {
    if (reloadForNewBuild(win, storage, clock())) event.preventDefault()
  })
}
